import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid2,
	IconButton,
	Snackbar,
	Tooltip,
	Typography,
} from "@mui/material";
import GlobalStyles from "@mui/material/GlobalStyles";
import { DocumentViewerModal } from "@/shared/components/DocumentViewerModal/DocumentViewerModal";
import type { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Assembly } from "@/pages/asset-details/models/Assembly";
import {
	addLinkAssetDocument,
	addLinkAssetPart,
	getAssetDocuments,
	getAssetParts,
	removeDocumentLink,
	removePartLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { CatalogFreshness } from "@/shared/catalogs/components/CatalogFreshness";
import {
	useCatalogFetchers,
	useCatalogReload,
	useCatalogUpdatedAt,
} from "@/shared/catalogs/hooks/useCatalogs";
import { AssemblySelectionTable } from "./AssemblySelectionTable";

interface Props {
	open: boolean;
	assembly: Assembly | null;
	onClose: (changed: boolean) => void;
}

// Keep one row per key value, in first-seen order. Used to collapse duplicate part codes /
// document names in the pickers (links are keyed by code, so a code needs a single pick row).
const dedupeByKey = (rows: any[], key: string): any[] => {
	const seen = new Set<string>();
	const out: any[] = [];
	for (const r of rows) {
		const v = r?.[key];
		if (v == null) continue;
		const k = String(v);
		if (seen.has(k)) continue;
		seen.add(k);
		out.push(r);
	}
	return out;
};

// The viewer shows the rendered image/PDF, so its extension must describe the path being shown, not
// the document's original type: a CAD original (DWG/CAL) still has a PDF/PNG rendition to display.
// Passing the original Extension made the viewer reject those docs (it only renders PDF/PNG/images).
const pickViewerSource = (doc: any): { path?: string; ext?: string } => {
	if (!doc) return {};
	if (doc.PDFPath) return { path: doc.PDFPath, ext: "PDF" };
	if (doc.PNGPath) return { path: doc.PNGPath, ext: "PNG" };
	if (doc.TMBPath) return { path: doc.TMBPath, ext: "PNG" };
	if (doc.DocPath)
		return { path: doc.DocPath, ext: String(doc.DocPath).split(".").pop() };
	return { path: undefined, ext: doc.Extension };
};

// The viewer only renders these formats. A drawing is previewable only when it resolves to one of
// them; docs with no uploaded version, or a CAD original with no rendition, have nothing to show.
const RENDERABLE_EXTS = new Set([
	"PNG",
	"JPG",
	"JPEG",
	"PDF",
	"GIF",
	"BMP",
	"SVG",
]);
const canViewDrawing = (doc: any): boolean => {
	const { path, ext } = pickViewerSource(doc);
	return !!path && !!ext && RENDERABLE_EXTS.has(ext.toUpperCase());
};

// A document is linkable only if it has a current version (a real uploaded file). The catalog
// (getDocumentsWeb) also returns version-less documents, but GetAssetDrawings (IsCurrent = 1) never
// returns those, so a link to one would be created yet vanish from every list — the "ticked it,
// saved, nothing happened" case. Offer only documents with a current version.
const hasCurrentVersion = (doc: any): boolean =>
	doc?.Version != null ||
	!!(doc?.DocPath || doc?.PDFPath || doc?.PNGPath || doc?.TMBPath);

// DOCS.Documents.DocumentName can carry trailing/leading spaces for some docs (dirty data) while
// AssetDrawings.DocumentName does not. SQL '=' ignores trailing spaces (so the tab count matches),
// but JS string comparison does not — which left such a linked drawing unticked and under-counted
// in the dialog. Normalize the name on every drawing row so the picker matches the link.
const normDrawRows = (rows: any[]): any[] =>
	rows.map((r) => ({
		...r,
		DocumentName: String(r?.DocumentName ?? "").trim(),
	}));

// Eye button for a drawing row. Opens the viewer on mousedown (not click): the table re-sorts
// selected-on-top on selection, so a click would move the row between mousedown and mouseup and
// never fire; stop propagation on both so an eye press never toggles the row selection.
const DrawingViewCell = ({
	data,
	onView,
}: {
	data: any;
	onView: (d: any) => void;
}) => {
	// Keep the latest data/onView in refs so the stable listeners always act on current values.
	const dataRef = useRef(data);
	dataRef.current = data;
	const onViewRef = useRef(onView);
	onViewRef.current = onView;
	const detachRef = useRef<(() => void) | null>(null);

	// Callback ref instead of useEffect+ref: AG Grid moves/swaps the cell DOM across the sort-to-top
	// reorder and row reuse (getRowId), which orphaned a useEffect listener and left the eye dead on
	// already-selected rows. Re-attaching on every node change keeps the handler on the live button.
	const attach = useCallback((el: HTMLButtonElement | null) => {
		if (detachRef.current) {
			detachRef.current();
			detachRef.current = null;
		}
		if (!el) return;
		// mousedown (not click): selecting re-sorts the row to the top, so a click's mouseup would
		// land off the moved button and never fire. stopPropagation keeps the eye from toggling the
		// row selection. Native listeners (not React) so they run before AG Grid's own handlers.
		const down = (e: Event) => {
			e.stopPropagation();
			onViewRef.current(dataRef.current);
		};
		const stop = (e: Event) => e.stopPropagation();
		el.addEventListener("mousedown", down);
		el.addEventListener("click", stop);
		detachRef.current = () => {
			el.removeEventListener("mousedown", down);
			el.removeEventListener("click", stop);
		};
	}, []);

	// No renderable rendition (no uploaded version, or a CAD original with no preview): show a muted,
	// non-clickable icon with a tooltip instead of an eye that would do nothing.
	if (!canViewDrawing(data)) {
		return (
			<Tooltip title="No preview available" arrow placement="top">
				<VisibilityOffIcon
					fontSize="small"
					sx={{ color: "action.disabled", cursor: "default" }}
				/>
			</Tooltip>
		);
	}

	return (
		<IconButton ref={attach} size="small" color="primary" title="View drawing">
			<VisibilityIcon fontSize="small" />
		</IconButton>
	);
};

// Manage an assembly's parts and drawings. An assembly is an asset (v2 model), so its parts and
// drawings are the normal AssetParts / AssetDrawings addressed by the assembly's own AssetId.
export const AssemblyDetailDialog = ({ open, assembly, onClose }: Props) => {
	const assemblyAssetId = assembly?.AssetId ?? null;

	const [allParts, setAllParts] = useState<any[]>([]);
	const [allDrawings, setAllDrawings] = useState<any[]>([]);
	const [currentPartNames, setCurrentPartNames] = useState<string[]>([]);
	const [currentDrawingNames, setCurrentDrawingNames] = useState<string[]>([]);
	// Header counts = distinct linked items the picker actually lists (baseline ∩ catalog), so the
	// "Parts (N)" / "Drawings (N)" header equals the ticked rows.
	const [currentPartsTotal, setCurrentPartsTotal] = useState(0);
	const [currentDrawingsTotal, setCurrentDrawingsTotal] = useState(0);
	const [selectedParts, setSelectedParts] = useState<any[]>([]);
	const [selectedDrawings, setSelectedDrawings] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [savingParts, setSavingParts] = useState(false);
	const [savingDrawings, setSavingDrawings] = useState(false);
	const [error, setError] = useState("");
	const [changed, setChanged] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [viewerDoc, setViewerDoc] = useState<any | null>(null);
	const [discardOpen, setDiscardOpen] = useState(false);
	const { fetchPartsInventory, fetchDocumentsCatalog } = useCatalogFetchers();
	const { reloadPartsInventory, reloadDocumentsCatalog } = useCatalogReload();
	const { partsUpdatedAt, documentsUpdatedAt } = useCatalogUpdatedAt();

	const load = useCallback(async () => {
		if (!assemblyAssetId) return;
		setLoading(true);
		// The two catalogs come from the shared cache: preloaded when the application opened, so
		// this dialog does not download them again on every open.
		const [rawPartsRows, rawDrawRows, curParts, curDraw] = await Promise.all([
			fetchPartsInventory().catch((e: any) => {
				setError(e?.message || "Error fetching the parts inventory");
				return [];
			}),
			fetchDocumentsCatalog().catch((e: any) => {
				setError(e?.message || "Error fetching the documents catalog");
				return [];
			}),
			getAssetParts(assemblyAssetId, false),
			getAssetDocuments(assemblyAssetId, false),
		]);
		// Links are keyed by Name / DocumentName, but the inventory and document catalog can hold
		// several rows with the same code (e.g. one part code in two bins). Collapse to one row per
		// code so a linked code checks a single row — otherwise the picker shows more ticks than the
		// real link count (13 links looked like 15 ticks when 2 codes were duplicated).
		const partsRows = dedupeByKey(rawPartsRows, "Name");
		// Exclude version-less documents (no uploaded file) so they cannot be linked: they would
		// disappear after saving, since GetAssetDrawings only returns drawings with a current version.
		const drawRows = dedupeByKey(
			normDrawRows(rawDrawRows),
			"DocumentName",
		).filter(hasCurrentVersion);
		setAllParts(partsRows);
		setAllDrawings(drawRows);
		const cp = curParts.ok ? (curParts.data?.tables?.[0]?.rows ?? []) : [];
		const cd = normDrawRows(
			curDraw.ok ? (curDraw.data?.tables?.[0]?.rows ?? []) : [],
		);
		const partNames = cp.map((r: any) => r.Name).filter(Boolean);
		const drawNames = cd.map((r: any) => r.DocumentName).filter(Boolean);
		setCurrentPartNames(partNames);
		setCurrentDrawingNames(drawNames);
		// The picker can only show/tick codes that exist in the inventory / document catalog. Count
		// the header from baseline ∩ catalog so "Parts (N)" / "Drawings (N)" equals the ticked rows,
		// even when a linked item is not listable here (e.g. an inactive/obsolete document, or a code
		// duplicated in the catalog). Also collapses the GetAssetDrawings name multiplication.
		const availParts = new Set(
			partsRows.map((r: any) => r.Name).filter(Boolean),
		);
		const availDraw = new Set(
			drawRows.map((r: any) => r.DocumentName).filter(Boolean),
		);
		setCurrentPartsTotal(
			new Set(partNames.filter((n: string) => availParts.has(n))).size,
		);
		setCurrentDrawingsTotal(
			new Set(drawNames.filter((n: string) => availDraw.has(n))).size,
		);
		// Seed the selection with ONLY the baseline items the picker actually lists, so it
		// matches the "changed" baseline exactly and the Save buttons start disabled — no
		// flicker where the button is briefly enabled until the grid finishes selecting.
		setSelectedParts(cp.filter((p: any) => availParts.has(p.Name)));
		setSelectedDrawings(cd.filter((d: any) => availDraw.has(d.DocumentName)));
		setLoading(false);
	}, [assemblyAssetId, fetchPartsInventory, fetchDocumentsCatalog]);

	useEffect(() => {
		if (open && assemblyAssetId) {
			// Reset baseline + selection together so "changed" is false during (re)load.
			setCurrentPartNames([]);
			setCurrentDrawingNames([]);
			setCurrentPartsTotal(0);
			setCurrentDrawingsTotal(0);
			setSelectedParts([]);
			setSelectedDrawings([]);
			setChanged(false);
			load();
		}
	}, [open, assemblyAssetId, load]);

	const partColumns = useMemo<ColDef[]>(
		() => [
			{
				field: "Name",
				headerName: "Part",
				minWidth: 120,
				maxWidth: 160,
				flex: 2,
			},
			{ field: "Description", headerName: "Description", flex: 4 },
			{
				field: "BinName",
				headerName: "Bin",
				minWidth: 90,
				maxWidth: 120,
				flex: 1,
			},
			{
				field: "Qty",
				headerName: "Qty",
				minWidth: 70,
				maxWidth: 80,
				flex: 1,
				type: "numericColumn",
			},
		],
		[],
	);

	const openViewer = useCallback((doc: any) => setViewerDoc(doc), []);

	const drawingColumns = useMemo<ColDef[]>(
		() => [
			{
				field: "DocumentName",
				headerName: "Document",
				minWidth: 130,
				maxWidth: 200,
				flex: 2,
			},
			{ field: "Title", headerName: "Title", flex: 4 },
			{
				headerName: "",
				colId: "view",
				minWidth: 46,
				maxWidth: 46,
				sortable: false,
				filter: false,
				resizable: false,
				cellRenderer: (p: any) => (
					<DrawingViewCell data={p.data} onView={openViewer} />
				),
			},
		],
		[openViewer],
	);

	// Compare only against baseline items that the picker actually lists (baseline ∩ available):
	// a linked resource the picker cannot show (e.g. a filtered / historical drawing) is not
	// selectable here, so it must not read as a pending change nor be removed on save.
	const partsChanged = useMemo(() => {
		const available = new Set(allParts.map((p) => p.Name).filter(Boolean));
		const sel = new Set(selectedParts.map((p) => p.Name).filter(Boolean));
		const cur = new Set(currentPartNames.filter((n) => available.has(n)));
		if (sel.size !== cur.size) return true;
		for (const n of sel) if (!cur.has(n as string)) return true;
		return false;
	}, [selectedParts, currentPartNames, allParts]);

	const drawingsChanged = useMemo(() => {
		const available = new Set(
			allDrawings.map((d) => d.DocumentName).filter(Boolean),
		);
		const sel = new Set(
			selectedDrawings.map((d) => d.DocumentName).filter(Boolean),
		);
		const cur = new Set(currentDrawingNames.filter((n) => available.has(n)));
		if (sel.size !== cur.size) return true;
		for (const n of sel) if (!cur.has(n as string)) return true;
		return false;
	}, [selectedDrawings, currentDrawingNames, allDrawings]);

	const handleSaveParts = async () => {
		if (!assemblyAssetId) return;
		setSavingParts(true);
		try {
			const available = new Set(
				allParts.map((p) => p.Name).filter(Boolean) as string[],
			);
			const sel = new Set(
				selectedParts.map((p) => p.Name).filter(Boolean) as string[],
			);
			// Only add/remove within the pickable set; leave links the picker can't show intact.
			const cur = new Set(currentPartNames.filter((n) => available.has(n)));
			const add = [...sel].filter((n) => !cur.has(n));
			const rem = [...cur].filter((n) => !sel.has(n));
			if (add.length) {
				const r = await addLinkAssetPart(assemblyAssetId, add, null);
				if (!r.ok) throw new Error(r.message);
			}
			if (rem.length) {
				const r = await removePartLink(assemblyAssetId, rem);
				if (!r.ok) throw new Error(r.message);
			}
			// Re-sync the baseline + header count from the DB. Header counts baseline ∩ catalog so it
			// equals the ticked rows (a linked part not listable in the picker is not counted here).
			const refreshed = await getAssetParts(assemblyAssetId, false);
			const rp = refreshed.ok ? (refreshed.data?.tables?.[0]?.rows ?? []) : [];
			const rpNames = rp.map((r: any) => r.Name).filter(Boolean);
			setCurrentPartNames(rpNames);
			setCurrentPartsTotal(
				new Set(rpNames.filter((n: string) => available.has(n))).size,
			);
			setChanged(true);
			setSuccessMessage("Parts saved successfully");
		} catch (e: any) {
			setError(e?.message || "Failed to save parts");
		} finally {
			setSavingParts(false);
		}
	};

	const handleSaveDrawings = async () => {
		if (!assemblyAssetId) return;
		setSavingDrawings(true);
		try {
			const available = new Set(
				allDrawings.map((d) => d.DocumentName).filter(Boolean) as string[],
			);
			const sel = new Set(
				selectedDrawings.map((d) => d.DocumentName).filter(Boolean) as string[],
			);
			// Only add/remove within the pickable set; leave links the picker can't show intact.
			const cur = new Set(currentDrawingNames.filter((n) => available.has(n)));
			const add = [...sel].filter((n) => !cur.has(n));
			const rem = [...cur].filter((n) => !sel.has(n));
			if (add.length) {
				const r = await addLinkAssetDocument(assemblyAssetId, add, null);
				if (!r.ok) throw new Error(r.message);
			}
			for (const doc of rem) {
				const r = await removeDocumentLink(assemblyAssetId, doc);
				if (!r.ok) throw new Error(r.message);
			}
			// Re-sync from the DB. Header counts baseline ∩ catalog so it equals the ticked rows (a
			// linked drawing not listable in the picker, e.g. inactive/obsolete, is not counted here).
			const refreshed = await getAssetDocuments(assemblyAssetId, false);
			const rd = normDrawRows(
				refreshed.ok ? (refreshed.data?.tables?.[0]?.rows ?? []) : [],
			);
			const rdNames = rd.map((r: any) => r.DocumentName).filter(Boolean);
			setCurrentDrawingNames(rdNames);
			setCurrentDrawingsTotal(
				new Set(rdNames.filter((n: string) => available.has(n))).size,
			);
			setChanged(true);
			setSuccessMessage("Drawings saved successfully");
		} catch (e: any) {
			setError(e?.message || "Failed to save drawings");
		} finally {
			setSavingDrawings(false);
		}
	};

	// Guard against losing unsaved selections when closing (button, X, or backdrop).
	const attemptClose = () => {
		if (partsChanged || drawingsChanged) {
			setDiscardOpen(true);
		} else {
			onClose(changed);
		}
	};

	const title = assembly?.AssetName ?? "Assembly";

	return (
		<>
			<GlobalStyles
				styles={{
					// MesfModal caps at maxWidth 'xl' and does not forward sx/fullWidth,
					// so widen this specific modal by targeting its paper via aria-labelledby.
					".MuiDialog-paper[aria-labelledby='assembly-detail-modal']": {
						width: "95vw",
						maxWidth: "1800px",
					},
				}}
			/>
			<MesfModal
				title={title}
				open={open}
				id="assembly-detail-modal"
				maxWidth="xl"
				handleClose={attemptClose}
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={2}>
						{/* PARTS */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
									Parts
								</Typography>
								<Typography variant="body2" color="text.secondary">
									({currentPartsTotal})
								</Typography>
							</Box>
							<AssemblySelectionTable
								rows={allParts}
								idField="Name"
								columns={partColumns}
								selectedIds={currentPartNames}
								onSelectionChanged={setSelectedParts}
								isLoading={loading}
								height="48vh"
								searchPlaceholder="Search parts"
							/>
							<CatalogFreshness
								updatedAt={partsUpdatedAt}
								onReload={async () => {
									setAllParts(
										dedupeByKey(await reloadPartsInventory(), "Name"),
									);
								}}
								onError={setError}
							/>
							<Grid2 container justifyContent="flex-end" sx={{ mt: 1 }}>
								<Grid2 size={{ md: 4, xs: 6 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={handleSaveParts}
										disabled={savingParts || loading || !partsChanged}
										startIcon={
											savingParts ? (
												<CircularProgress size="1rem" color="inherit" />
											) : undefined
										}
									>
										Save Parts
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>

						{/* DRAWINGS */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
									Drawings
								</Typography>
								<Typography variant="body2" color="text.secondary">
									({currentDrawingsTotal})
								</Typography>
							</Box>
							<AssemblySelectionTable
								rows={allDrawings}
								idField="DocumentName"
								columns={drawingColumns}
								selectedIds={currentDrawingNames}
								onSelectionChanged={setSelectedDrawings}
								isLoading={loading}
								height="48vh"
								searchPlaceholder="Search drawings"
							/>
							<CatalogFreshness
								updatedAt={documentsUpdatedAt}
								onReload={async () => {
									setAllDrawings(
										dedupeByKey(
											normDrawRows(await reloadDocumentsCatalog()),
											"DocumentName",
										).filter(hasCurrentVersion),
									);
								}}
								onError={setError}
							/>
							<Grid2 container justifyContent="flex-end" sx={{ mt: 1 }}>
								<Grid2 size={{ md: 4, xs: 6 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={handleSaveDrawings}
										disabled={savingDrawings || loading || !drawingsChanged}
										startIcon={
											savingDrawings ? (
												<CircularProgress size="1rem" color="inherit" />
											) : undefined
										}
									>
										Save Drawings
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions sx={{ px: 2, pt: 1, pb: 2 }}>
					<Grid2 container justifyContent="flex-end" width="100%">
						<Grid2 size={{ xs: 6, sm: 3, md: 2 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={attemptClose}
							>
								Close
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<MesfModal
				title="Unsaved selections"
				open={discardOpen}
				maxWidth="sm"
				handleClose={() => setDiscardOpen(false)}
			>
				<MesfModal.Content dividers>
					<Typography>
						You have parts or drawings selected that have not been saved. If you
						close now, those selections will be lost.
					</Typography>
				</MesfModal.Content>
				<MesfModal.Actions sx={{ px: 2, pt: 1, pb: 2 }}>
					<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
						<Grid2 size={{ xs: 6, md: 4 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => {
									setDiscardOpen(false);
									onClose(changed);
								}}
							>
								Discard & Close
							</Button>
						</Grid2>
						<Grid2 size={{ xs: 6, md: 4 }}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								onClick={() => setDiscardOpen(false)}
							>
								Keep editing
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<DocumentViewerModal
				open={!!viewerDoc}
				onClose={() => setViewerDoc(null)}
				title={viewerDoc?.Title || viewerDoc?.DocumentName || "Document"}
				documentPath={pickViewerSource(viewerDoc).path}
				extension={pickViewerSource(viewerDoc).ext}
			/>
			<Snackbar
				open={!!successMessage}
				autoHideDuration={4000}
				onClose={() => setSuccessMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setSuccessMessage("")}>
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
